import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, RefreshCw, Plus, ChevronDown, ChevronUp,
  CheckCircle, XCircle, AlertTriangle, Shield, Layers,
  Users, Store, UserCheck, Settings, Calculator, History,
  Play, Archive, Edit, Eye
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { API_URL } from '../../utils/apiConfig';

const ScopeBadge = ({ scope }) => {
  const colors = {
    system: 'bg-purple-600 text-purple-100',
    sender: 'bg-blue-600 text-blue-100',
    merchant: 'bg-emerald-600 text-emerald-100',
    beneficiary: 'bg-amber-600 text-amber-100',
  };
  const icons = {
    system: <Settings className="w-3 h-3" />,
    sender: <Users className="w-3 h-3" />,
    merchant: <Store className="w-3 h-3" />,
    beneficiary: <UserCheck className="w-3 h-3" />,
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium uppercase flex items-center gap-1 ${colors[scope] || 'bg-slate-600'}`}>
      {icons[scope]} {scope}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const colors = {
    draft: 'bg-slate-600 text-slate-200',
    scheduled: 'bg-blue-600 text-blue-100',
    active: 'bg-emerald-600 text-emerald-100',
    superseded: 'bg-amber-600 text-amber-100',
    archived: 'bg-red-600 text-red-100',
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${colors[status] || 'bg-slate-600'}`}>
      {status}
    </span>
  );
};

const LimitTypeLabel = ({ type }) => {
  const labels = {
    per_transaction: 'Per Transaction',
    daily_cumulative: 'Daily Cumulative',
    monthly_cumulative: 'Monthly Cumulative',
    daily_count: 'Daily Count',
    monthly_count: 'Monthly Count',
  };
  return <span>{labels[type] || type}</span>;
};

export default function AdminLimitsView() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [rules, setRules] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [expandedId, setExpandedId] = useState(null);
  const [scopeFilter, setScopeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError] = useState('');
  
  // Calculator state
  const [calcAmount, setCalcAmount] = useState('50');
  const [calcSenderId, setCalcSenderId] = useState('');
  const [calcMerchantId, setCalcMerchantId] = useState('');
  const [calcResult, setCalcResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  
  // Create rule state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    scope: 'sender',
    name: '',
    description: '',
    target_id: '',
    reason: '',
    effective_from: new Date().toISOString().slice(0, 16),
    limits: [
      { limit_type: 'per_transaction', min_value: 10, max_value: 200 },
    ]
  });
  const [creating, setCreating] = useState(false);

  const getToken = () => localStorage.getItem('pecash_token');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/limits/dashboard`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    }
  }, []);

  const fetchRules = useCallback(async () => {
    try {
      let url = `${API_URL}/api/admin/limits/rules?limit=100`;
      if (scopeFilter) url += `&scope=${scopeFilter}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRules(data.rules || []);
      }
    } catch (err) {
      console.error('Rules fetch error:', err);
    }
  }, [scopeFilter, statusFilter]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/limits/history?limit=50`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
      }
    } catch (err) {
      console.error('History fetch error:', err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchRules(), fetchHistory()]);
      setLoading(false);
    };
    load();
  }, [fetchDashboard, fetchRules, fetchHistory]);

  useEffect(() => {
    fetchRules();
  }, [scopeFilter, statusFilter, fetchRules]);

  const handleCalculate = async () => {
    setCalcLoading(true);
    setCalcResult(null);
    setError('');
    
    try {
      let url = `${API_URL}/api/admin/limits/calculator?amount_usd=${calcAmount}`;
      if (calcSenderId) url += `&sender_id=${calcSenderId}`;
      if (calcMerchantId) url += `&merchant_id=${calcMerchantId}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCalcResult(data);
      } else {
        const err = await res.json();
        setError(err.detail || 'Calculation failed');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setCalcLoading(false);
    }
  };

  const handleCreateRule = async () => {
    setCreating(true);
    setError('');
    
    try {
      const body = {
        ...createForm,
        target_id: createForm.scope === 'system' ? null : createForm.target_id,
      };
      
      const res = await fetch(`${API_URL}/api/admin/limits/rules`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        setShowCreateModal(false);
        setCreateForm({
          scope: 'sender',
          name: '',
          description: '',
          target_id: '',
          reason: '',
          effective_from: new Date().toISOString().slice(0, 16),
          limits: [{ limit_type: 'per_transaction', min_value: 10, max_value: 200 }]
        });
        await fetchRules();
        setActiveTab('rules');
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to create rule');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setCreating(false);
    }
  };

  const handleActivate = async (ruleId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/limits/rules/${ruleId}/activate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (res.ok) {
        await Promise.all([fetchDashboard(), fetchRules()]);
      } else {
        const err = await res.json();
        setError(err.detail || 'Activation failed');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleArchive = async (ruleId) => {
    const reason = prompt('Enter reason for archiving this rule:');
    if (!reason || reason.length < 5) {
      setError('Reason must be at least 5 characters');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/limits/rules/${ruleId}/archive?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (res.ok) {
        await Promise.all([fetchDashboard(), fetchRules()]);
      } else {
        const err = await res.json();
        setError(err.detail || 'Archive failed');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const addLimitToForm = () => {
    setCreateForm(prev => ({
      ...prev,
      limits: [...prev.limits, { limit_type: 'daily_cumulative', max_value: 500 }]
    }));
  };

  const removeLimitFromForm = (index) => {
    setCreateForm(prev => ({
      ...prev,
      limits: prev.limits.filter((_, i) => i !== index)
    }));
  };

  const updateLimitInForm = (index, field, value) => {
    setCreateForm(prev => ({
      ...prev,
      limits: prev.limits.map((l, i) => i === index ? { ...l, [field]: value } : l)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Shield className="w-6 h-6 text-purple-400" />
                Transaction Limits
              </h1>
              <p className="text-sm text-slate-400">Velocity controls & spending limits</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {dashboard?.pilot_mode && (
              <span className="px-3 py-1 bg-amber-900/50 border border-amber-700 rounded-full text-amber-300 text-xs font-medium">
                PILOT MODE
              </span>
            )}
            <span className="px-3 py-1 bg-purple-900/50 border border-purple-700 rounded-full text-purple-300 text-xs font-medium">
              CEILING: ${dashboard?.pilot_ceiling_usd}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800 border border-purple-700/50 rounded-xl p-4"
            >
              <p className="text-purple-400 text-xs mb-1 flex items-center gap-1">
                <Settings className="w-3 h-3" /> System Rules
              </p>
              <p className="text-2xl font-bold text-white">{dashboard.scope_counts?.system || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-slate-800 border border-blue-700/50 rounded-xl p-4"
            >
              <p className="text-blue-400 text-xs mb-1 flex items-center gap-1">
                <Users className="w-3 h-3" /> Sender Rules
              </p>
              <p className="text-2xl font-bold text-white">{dashboard.scope_counts?.sender || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-slate-800 border border-emerald-700/50 rounded-xl p-4"
            >
              <p className="text-emerald-400 text-xs mb-1 flex items-center gap-1">
                <Store className="w-3 h-3" /> Merchant Rules
              </p>
              <p className="text-2xl font-bold text-white">{dashboard.scope_counts?.merchant || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-800 border border-amber-700/50 rounded-xl p-4"
            >
              <p className="text-amber-400 text-xs mb-1 flex items-center gap-1">
                <UserCheck className="w-3 h-3" /> Beneficiary Rules
              </p>
              <p className="text-2xl font-bold text-white">{dashboard.scope_counts?.beneficiary || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-slate-800 border border-red-700/50 rounded-xl p-4"
            >
              <p className="text-red-400 text-xs mb-1">Failed Today</p>
              <p className="text-2xl font-bold text-white">{dashboard.failed_evaluations_today || 0}</p>
            </motion.div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-xl p-4 mb-6">
            <p className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {error}
            </p>
            <Button size="sm" variant="ghost" className="mt-2 text-red-400" onClick={() => setError('')}>
              Dismiss
            </Button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4 flex-wrap">
          <Button
            data-testid="limits-tab-dashboard"
            variant={activeTab === 'dashboard' ? 'default' : 'ghost'}
            className={activeTab === 'dashboard' ? 'bg-purple-600' : 'text-slate-400'}
            onClick={() => setActiveTab('dashboard')}
          >
            <Layers className="w-4 h-4 mr-1" /> Dashboard
          </Button>
          <Button
            data-testid="limits-tab-rules"
            variant={activeTab === 'rules' ? 'default' : 'ghost'}
            className={activeTab === 'rules' ? 'bg-purple-600' : 'text-slate-400'}
            onClick={() => setActiveTab('rules')}
          >
            <Shield className="w-4 h-4 mr-1" /> Rules ({rules.length})
          </Button>
          <Button
            data-testid="limits-tab-calculator"
            variant={activeTab === 'calculator' ? 'default' : 'ghost'}
            className={activeTab === 'calculator' ? 'bg-purple-600' : 'text-slate-400'}
            onClick={() => setActiveTab('calculator')}
          >
            <Calculator className="w-4 h-4 mr-1" /> Calculator
          </Button>
          <Button
            data-testid="limits-tab-history"
            variant={activeTab === 'history' ? 'default' : 'ghost'}
            className={activeTab === 'history' ? 'bg-purple-600' : 'text-slate-400'}
            onClick={() => setActiveTab('history')}
          >
            <History className="w-4 h-4 mr-1" /> History
          </Button>
          <Button
            data-testid="limits-create-rule"
            variant="ghost"
            className="text-emerald-400 ml-auto"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus className="w-4 h-4 mr-1" /> Create Rule
          </Button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboard && (
          <div className="space-y-6">
            {/* Default Limits Card */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-purple-400" />
                Default System Limits (Pilot Mode)
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-slate-900 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">Per Transaction Max</p>
                  <p className="text-white font-bold">${dashboard.default_limits?.per_transaction_max}</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">Daily Cumulative</p>
                  <p className="text-white font-bold">${dashboard.default_limits?.daily_cumulative}</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">Monthly Cumulative</p>
                  <p className="text-white font-bold">${dashboard.default_limits?.monthly_cumulative}</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">Daily Count</p>
                  <p className="text-white font-bold">{dashboard.default_limits?.daily_count} txns</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-3">
                  <p className="text-slate-400 text-xs">Monthly Count</p>
                  <p className="text-white font-bold">{dashboard.default_limits?.monthly_count} txns</p>
                </div>
              </div>
            </div>

            {/* Hierarchy Visualization */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4">Limit Hierarchy (Most Restrictive Wins)</h3>
              <div className="flex items-center justify-center gap-4 py-4">
                <div className="flex flex-col items-center">
                  <div className="w-24 h-24 rounded-full bg-purple-900/50 border-2 border-purple-500 flex items-center justify-center">
                    <Settings className="w-8 h-8 text-purple-400" />
                  </div>
                  <p className="text-purple-400 mt-2 font-medium">SYSTEM</p>
                  <p className="text-slate-500 text-xs">Global Ceiling</p>
                </div>
                <ChevronDown className="w-6 h-6 text-slate-500 rotate-[-90deg]" />
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 rounded-full bg-blue-900/50 border-2 border-blue-500 flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-400" />
                  </div>
                  <p className="text-blue-400 mt-2 font-medium">SUPPORTER</p>
                  <p className="text-slate-500 text-xs">Per User</p>
                </div>
                <ChevronDown className="w-6 h-6 text-slate-500 rotate-[-90deg]" />
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-900/50 border-2 border-emerald-500 flex items-center justify-center">
                    <Store className="w-5 h-5 text-emerald-400" />
                  </div>
                  <p className="text-emerald-400 mt-2 font-medium">MERCHANT</p>
                  <p className="text-slate-500 text-xs">Per Colmado</p>
                </div>
                <ChevronDown className="w-6 h-6 text-slate-500 rotate-[-90deg]" />
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-amber-900/50 border-2 border-amber-500 flex items-center justify-center">
                    <UserCheck className="w-4 h-4 text-amber-400" />
                  </div>
                  <p className="text-amber-400 mt-2 font-medium">BENEFICIARY</p>
                  <p className="text-slate-500 text-xs">Most Specific</p>
                </div>
              </div>
              <p className="text-center text-slate-500 text-sm mt-4">
                Lower-level rules can only be MORE restrictive than system ceiling
              </p>
            </div>

            {/* Recent Evaluations */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4">Recent Limit Evaluations</h3>
              {dashboard.recent_evaluations?.length > 0 ? (
                <div className="space-y-2">
                  {dashboard.recent_evaluations.map((e, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-900 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        {e.allowed ? (
                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-400" />
                        )}
                        <div>
                          <p className="text-white text-sm">${e.amount_usd?.toFixed(2)}</p>
                          <p className="text-slate-500 text-xs">Sender: {e.sender_id?.slice(0, 8)}...</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={e.allowed ? 'text-emerald-400 text-sm' : 'text-red-400 text-sm'}>
                          {e.allowed ? 'PASSED' : 'FAILED'}
                        </p>
                        <p className="text-slate-500 text-xs">{new Date(e.timestamp).toLocaleTimeString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">No recent evaluations</p>
              )}
            </div>
          </div>
        )}

        {/* Rules Tab */}
        {activeTab === 'rules' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex gap-4 mb-4">
              <select
                value={scopeFilter}
                onChange={(e) => setScopeFilter(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">All Scopes</option>
                <option value="system">System</option>
                <option value="sender">Supporter</option>
                <option value="merchant">Merchant</option>
                <option value="beneficiary">Beneficiary</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="active">Active</option>
                <option value="superseded">Superseded</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Rules List */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Rule</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Scope</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Target</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Status</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Limits</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {rules.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-slate-500 py-8">
                        No limit rules found. Create one to get started.
                      </td>
                    </tr>
                  ) : (
                    rules.map((rule) => (
                      <React.Fragment key={rule.id}>
                        <tr 
                          className="hover:bg-slate-700/50 cursor-pointer"
                          onClick={() => setExpandedId(expandedId === rule.id ? null : rule.id)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {expandedId === rule.id ? (
                                <ChevronUp className="w-4 h-4 text-slate-500" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-slate-500" />
                              )}
                              <div>
                                <p className="text-white text-sm font-medium">{rule.name}</p>
                                <p className="text-slate-500 text-xs">v{rule.version}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3"><ScopeBadge scope={rule.scope} /></td>
                          <td className="px-4 py-3 text-slate-400 text-sm">
                            {rule.target_id ? `${rule.target_id.slice(0, 12)}...` : '-'}
                          </td>
                          <td className="px-4 py-3"><StatusBadge status={rule.status} /></td>
                          <td className="px-4 py-3 text-slate-400 text-sm">
                            {rule.limits?.length || 0} configured
                          </td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <div className="flex gap-2">
                              {rule.status === 'draft' && (
                                <Button
                                  size="sm"
                                  className="text-xs bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => handleActivate(rule.id)}
                                >
                                  <Play className="w-3 h-3 mr-1" /> Activate
                                </Button>
                              )}
                              {['draft', 'active', 'scheduled'].includes(rule.status) && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  className="text-xs"
                                  onClick={() => handleArchive(rule.id)}
                                >
                                  <Archive className="w-3 h-3 mr-1" /> Archive
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {expandedId === rule.id && (
                          <tr className="bg-slate-900">
                            <td colSpan="6" className="px-4 py-4">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                                <div>
                                  <p className="text-slate-500">Rule ID</p>
                                  <p className="text-white font-mono text-xs">{rule.id}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Created By</p>
                                  <p className="text-white">{rule.created_by?.slice(0, 12)}...</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Effective From</p>
                                  <p className="text-white">{rule.effective_from?.slice(0, 16)}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Reason</p>
                                  <p className="text-white">{rule.reason || '-'}</p>
                                </div>
                              </div>
                              <div className="border-t border-slate-700 pt-4">
                                <p className="text-slate-400 text-sm mb-2">Configured Limits:</p>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                  {rule.limits?.map((limit, i) => (
                                    <div key={i} className="bg-slate-800 rounded p-2">
                                      <p className="text-slate-500 text-xs"><LimitTypeLabel type={limit.limit_type} /></p>
                                      <p className="text-white font-medium">
                                        {limit.limit_type === 'per_transaction' 
                                          ? `$${limit.min_value || 0} - $${limit.max_value}`
                                          : limit.limit_type.includes('count')
                                            ? `${limit.max_value} txns`
                                            : `$${limit.max_value}`
                                        }
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Calculator Tab */}
        {activeTab === 'calculator' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-purple-400" />
                Limit Calculator (Simulation Only)
              </h3>
              <p className="text-slate-400 text-sm mb-6">
                Test what limits would apply to a hypothetical transaction. This does NOT create any transaction or consume limits.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Amount USD</label>
                  <input
                    type="number"
                    value={calcAmount}
                    onChange={(e) => setCalcAmount(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    placeholder="50.00"
                    min="1"
                    max="200"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Sender ID (optional)</label>
                  <input
                    type="text"
                    value={calcSenderId}
                    onChange={(e) => setCalcSenderId(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    placeholder="UUID of sender"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Merchant ID (optional)</label>
                  <input
                    type="text"
                    value={calcMerchantId}
                    onChange={(e) => setCalcMerchantId(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    placeholder="UUID of merchant"
                  />
                </div>
                
                <Button
                  onClick={handleCalculate}
                  disabled={calcLoading || !calcAmount}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  {calcLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Calculator className="w-4 h-4 mr-2" />
                  )}
                  Calculate Limits
                </Button>
              </div>

              {calcResult && (
                <div className="mt-6 pt-6 border-t border-slate-700">
                  <div className={`rounded-lg p-4 mb-4 ${calcResult.result === 'WOULD_PASS' ? 'bg-emerald-900/30 border border-emerald-700' : 'bg-red-900/30 border border-red-700'}`}>
                    <div className="flex items-center gap-2">
                      {calcResult.result === 'WOULD_PASS' ? (
                        <CheckCircle className="w-6 h-6 text-emerald-400" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-400" />
                      )}
                      <span className={`text-lg font-bold ${calcResult.result === 'WOULD_PASS' ? 'text-emerald-400' : 'text-red-400'}`}>
                        {calcResult.result}
                      </span>
                    </div>
                    <p className="text-slate-400 text-sm mt-2">
                      Controlling Rule: {calcResult.controlling_rule}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-slate-900 rounded-lg p-3">
                      <p className="text-slate-500">Per Transaction</p>
                      <p className="text-white">${calcResult.effective_limits?.per_transaction?.min} - ${calcResult.effective_limits?.per_transaction?.max}</p>
                    </div>
                    <div className="bg-slate-900 rounded-lg p-3">
                      <p className="text-slate-500">Daily Cumulative</p>
                      <p className="text-white">${calcResult.effective_limits?.daily_cumulative}</p>
                    </div>
                    <div className="bg-slate-900 rounded-lg p-3">
                      <p className="text-slate-500">Monthly Cumulative</p>
                      <p className="text-white">${calcResult.effective_limits?.monthly_cumulative}</p>
                    </div>
                    <div className="bg-slate-900 rounded-lg p-3">
                      <p className="text-slate-500">Daily Count</p>
                      <p className="text-white">{calcResult.effective_limits?.daily_count} txns</p>
                    </div>
                  </div>

                  {calcResult.violations?.length > 0 && (
                    <div className="mt-4 bg-red-900/20 border border-red-700 rounded-lg p-3">
                      <p className="text-red-400 font-medium mb-2">Violations:</p>
                      {calcResult.violations.map((v, i) => (
                        <p key={i} className="text-red-300 text-sm">{v.message}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-900 border-b border-slate-700">
                <tr>
                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Timestamp</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Action</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Rule ID</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Performed By</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {history.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center text-slate-500 py-8">
                      No history entries
                    </td>
                  </tr>
                ) : (
                  history.map((h) => (
                    <tr key={h.id} className="hover:bg-slate-700/50">
                      <td className="px-4 py-3 text-slate-400 text-sm">{new Date(h.timestamp).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          h.action === 'created' ? 'bg-blue-600 text-blue-100' :
                          h.action === 'activated' ? 'bg-emerald-600 text-emerald-100' :
                          h.action === 'archived' ? 'bg-red-600 text-red-100' :
                          'bg-slate-600 text-slate-200'
                        }`}>
                          {h.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white font-mono text-xs">{h.rule_id?.slice(0, 12)}...</td>
                      <td className="px-4 py-3 text-slate-400 text-sm">{h.performed_by?.slice(0, 12)}...</td>
                      <td className="px-4 py-3 text-slate-400 text-sm">{h.reason || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Rule Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-white font-semibold mb-4">Create Limit Rule</h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Scope</label>
                      <select
                        value={createForm.scope}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, scope: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                      >
                        <option value="system">System (Global)</option>
                        <option value="sender">Supporter</option>
                        <option value="merchant">Merchant</option>
                        <option value="beneficiary">Beneficiary</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Name</label>
                      <input
                        type="text"
                        value={createForm.name}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                        placeholder="Rule name"
                      />
                    </div>
                  </div>

                  {createForm.scope !== 'system' && (
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Target ID</label>
                      <input
                        type="text"
                        value={createForm.target_id}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, target_id: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                        placeholder="Entity UUID"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Reason</label>
                    <textarea
                      value={createForm.reason}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, reason: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white h-20"
                      placeholder="Why is this limit rule being created?"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Effective From</label>
                    <input
                      type="datetime-local"
                      value={createForm.effective_from}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, effective_from: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-slate-400 text-sm">Limits Configuration</label>
                      <Button size="sm" variant="ghost" className="text-emerald-400" onClick={addLimitToForm}>
                        <Plus className="w-4 h-4 mr-1" /> Add Limit
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {createForm.limits.map((limit, i) => (
                        <div key={i} className="bg-slate-900 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <select
                              value={limit.limit_type}
                              onChange={(e) => updateLimitInForm(i, 'limit_type', e.target.value)}
                              className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white text-sm"
                            >
                              <option value="per_transaction">Per Transaction</option>
                              <option value="daily_cumulative">Daily Cumulative</option>
                              <option value="monthly_cumulative">Monthly Cumulative</option>
                              <option value="daily_count">Daily Count</option>
                              <option value="monthly_count">Monthly Count</option>
                            </select>
                            {createForm.limits.length > 1 && (
                              <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeLimitFromForm(i)}>
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {limit.limit_type === 'per_transaction' && (
                              <div>
                                <label className="text-slate-500 text-xs">Min Value</label>
                                <input
                                  type="number"
                                  value={limit.min_value || ''}
                                  onChange={(e) => updateLimitInForm(i, 'min_value', parseFloat(e.target.value) || 0)}
                                  className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white text-sm"
                                />
                              </div>
                            )}
                            <div>
                              <label className="text-slate-500 text-xs">Max Value</label>
                              <input
                                type="number"
                                value={limit.max_value || ''}
                                onChange={(e) => updateLimitInForm(i, 'max_value', parseFloat(e.target.value) || 0)}
                                className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white text-sm"
                                max={limit.limit_type === 'per_transaction' ? 200 : undefined}
                              />
                            </div>
                          </div>
                          {limit.limit_type === 'per_transaction' && limit.max_value > 200 && (
                            <p className="text-red-400 text-xs mt-1">Max cannot exceed $200 pilot ceiling</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-700">
                  <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateRule}
                    disabled={creating || !createForm.name || !createForm.reason || createForm.reason.length < 5}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {creating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    Create Rule (Draft)
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
